import { useMemo } from "react";
import { Dial, type DialHoverTarget } from "@keydials/ui";
import type { DialLayout, DialModel } from "@keydials/dial-renderer";
import { attributeScopeApplies, type FeedScopeEntry, type GeneratedNodeAttribute } from "@keydials/api-client";
import { DEFAULT_SEGMENT_NOUN, type SegmentNoun } from "@keydials/dial-workspace";
import {
  attributeFed,
  feedCoverage,
  feedTally,
  toggleAttribute,
  toggleItem,
  toggleSection,
  toggleSegment,
  type FeedPaintSection,
  type FeedPaintState,
} from "../../feedScopePaint";

/**
 * The sharer's paint canvas (KEY-257 phase 2): choose what this dial feeds by painting it onto the dial
 * itself, instead of reading a list of checkboxes and hoping they map onto the thing you can picture.
 *
 * **The canvas paints coverage, never status.** It answers "what am I sending", not "how is it going" —
 * so a wedge's colour here says fed / partly fed / not fed and nothing about whether the work is done.
 * The admin scope-paint canvas fought and won this argument already, and shares these exact tones on
 * purpose: coverage means the same thing in both places, so it should look the same in both.
 *
 * **Items are not on the dial.** A wedge has no geometry for one, and pretending otherwise is where
 * "simple" starts to leak — so they are tickable rows beneath it instead, listed only for the action
 * points actually being fed. Everything starts ticked: painting an action point means all of it, and
 * unticking is how a sharer holds something back.
 */

// Fixed hexes rather than theme tokens, matching the dial face itself, which keeps its own colours
// across light and dark.
const PAINT: Record<FeedPaintState, string> = { full: "#4f46e5", partial: "#a5b4fc", none: "#cbd5e1" };

export function FeedScopeCanvas({
  sections,
  scopes,
  attributes = [],
  openEnded,
  readonly = false,
  onChange,
  noun = DEFAULT_SEGMENT_NOUN,
}: {
  sections: FeedPaintSection[];
  scopes: FeedScopeEntry[];
  /** The dial's declared node attributes. Fields that cannot cross an organisation boundary are not
   * offered at all, rather than offered and refused on save. */
  attributes?: GeneratedNodeAttribute[];
  /** Whether newly painted sections promise whatever is added to them later. */
  openEnded: boolean;
  readonly?: boolean;
  onChange: (next: FeedScopeEntry[]) => void;
  /** This dial's word for an action point (KEY-539/KEY-721); the platform's word when omitted. */
  noun?: SegmentNoun;
}) {
  const coverage = useMemo(() => feedCoverage(sections, scopes), [sections, scopes]);

  const dialModel = useMemo<DialModel>(
    () => ({
      innerRadius: 120,
      midRadius: 300,
      outerRadius: 372,
      sectionGap: 0.012,
      showCentralProgress: false,
      sections: sections.map((section, si) => ({
        key: section.key,
        label: section.label,
        segments: section.segments.map((segment, gi) => ({
          key: segment.key,
          label: segment.label,
          colour: PAINT[coverage.sections[si]?.segments[gi]?.state ?? "none"],
        })),
      })),
    }),
    [sections, coverage],
  );

  const stateOf = (sectionKey: string, segmentKey?: string): FeedPaintState => {
    const section = coverage.sections.find((s) => s.key === sectionKey);
    if (!section) return "none";
    if (segmentKey === undefined) return section.state;
    return section.segments.find((s) => s.key === segmentKey)?.state ?? "none";
  };

  const label = (state: FeedPaintState) =>
    state === "full" ? "Feeding this" : state === "partial" ? "Feeding part of this" : "Not feeding this";

  const tooltip = (target: DialHoverTarget) => {
    const isSegment = target.kind === "segment";
    const state = stateOf(target.sectionKey, isSegment ? target.segmentKey : undefined);
    return (
      <div
        style={{
          background: "#1f2533",
          color: "#fff",
          borderRadius: 7,
          padding: "7px 10px",
          fontSize: 12,
          lineHeight: 1.45,
          maxWidth: 240,
          boxShadow: "0 4px 14px rgba(15,23,42,.35)",
        }}
      >
        <div style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{target.label}</div>
        <div style={{ opacity: 0.85 }}>{label(state)}</div>
        {!readonly && <div style={{ opacity: 0.7, fontStyle: "italic", marginTop: 2 }}>Click to toggle</div>}
      </div>
    );
  };

  // A dashed outline on partly-fed SECTION bands, so partial coverage survives being read without
  // colour — the three tones are close together by design and one of them is the "nothing here" grey.
  //
  // On the section band, not the wedges, because at this granularity a wedge cannot be partial: an
  // action point is fed or it isn't. (The admin canvas outlines wedges because a wedge there contains
  // items, which can be partly covered. Copying that here would have drawn an outline that never
  // appears.) Items arrive in a later phase; when they do, wedges gain a partial state and this outline
  // should extend to them.
  const overlay = (layout: DialLayout) => (
    <g pointerEvents="none">
      {layout.sectionArcs.map((arc) =>
        stateOf(arc.sectionKey) === "partial" ? (
          <path key={arc.sectionKey} d={arc.path} fill="none" stroke={PAINT.full} strokeWidth={3} strokeDasharray="9 6" />
        ) : null,
      )}
    </g>
  );

  // Fields a sharer may name on a fed action point. The kinds whose values are ids into this
  // organisation — files, images, videos and people — are filtered out rather than offered and refused
  // on save: the sharer should not be able to pick something that cannot travel.
  const UNFEEDABLE = ["Document", "Image", "Video", "User"];
  const segmentAttributes = (sectionKey: string, segmentKey: string) =>
    attributes.filter(
      (a) =>
        a.target === "Segment"
        && !UNFEEDABLE.includes(a.type)
        && attributeScopeApplies(a.target, a.appliesToKeys, sectionKey, segmentKey, ""),
    );

  // Every action point actually being fed, and nowhere else: a picker for something you have not offered
  // is noise, and the list would otherwise be the whole dial. Listed whether or not it HAS items — an
  // action point with none still has fields to offer, and gating the panel on items hid them entirely.
  const fedSegments = coverage.sections.flatMap((cs) => {
    const section = sections.find((s) => s.key === cs.key);
    if (!section) return [];
    return cs.segments
      .filter((cg) => cg.state === "full")
      .flatMap((cg) => {
        const segment = section.segments.find((g) => g.key === cg.key);
        if (!segment) return [];
        return [{
          section,
          segment,
          items: cg.items.map((i) => ({
            key: i.key,
            fed: i.fed,
            name: segment.items?.find((x) => x.key === i.key)?.name ?? i.key,
          })),
        }];
      });
  });

  if (sections.length === 0) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center" }}>
      <div style={{ width: "100%", maxWidth: 320 }}>
        <Dial
          model={dialModel}
          fluid
          ariaLabel="What this dial feeds — click a wedge to include or exclude it"
          onSelectSection={readonly ? undefined : (sectionKey) => onChange(toggleSection(sections, scopes, sectionKey, openEnded))}
          onSelectSegment={readonly ? undefined : (sectionKey, segmentKey) => onChange(toggleSegment(sections, scopes, sectionKey, segmentKey))}
          overlay={overlay}
          renderTooltip={tooltip}
        />
      </div>
      {/* The tally is the summary and the accessible one both. Announced rather than only shown: the
          canvas is the only place the choice is visible, so a change nobody can see is a change nobody
          can check. */}
      <div
        role="status"
        aria-live="polite"
        style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text)", textAlign: "center" }}
      >
        {feedTally(coverage, noun)}
      </div>
      <div style={{ fontSize: 11.5, color: "var(--text-faint)", textAlign: "center" }}>
        Colour shows what you are feeding, not how the work is going. Click a wedge for one {noun.one},
        or the outer band for a whole section.
      </div>

      {!readonly && fedSegments.some((f) => f.items.length > 0 || segmentAttributes(f.section.key, f.segment.key).length > 0) && (
        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)" }}>
            Detail on what you are feeding
          </div>
          {fedSegments.map(({ section, segment, items }) => (
            <div key={`${section.key}/${segment.key}`} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text)" }}>
                {section.label} · {segment.label}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, paddingLeft: 4 }}>
                {items.map((item) => (
                  <label key={item.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5 }}>
                    <input
                      type="checkbox"
                      checked={item.fed}
                      onChange={() => onChange(toggleItem(sections, scopes, section.key, segment.key, item.key))}
                    />
                    {item.name}
                  </label>
                ))}
              </div>
              {segmentAttributes(section.key, segment.key).length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, paddingLeft: 4, marginTop: 2 }}>
                  {segmentAttributes(section.key, segment.key).map((attr) => (
                    <label
                      key={attr.key}
                      style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-muted)" }}
                    >
                      <input
                        type="checkbox"
                        checked={attributeFed(scopes, section.key, segment.key, null, attr.key)}
                        onChange={() => onChange(toggleAttribute(scopes, section.key, segment.key, null, attr.key))}
                      />
                      {attr.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
          <p style={{ fontSize: 11.5, color: "var(--text-faint)", margin: 0 }}>
            Fields start off. Files, images, videos and people can&rsquo;t be fed to another organisation —
            they point at things only you can see — so they aren&rsquo;t offered here.
          </p>
        </div>
      )}
    </div>
  );
}
